import { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { StudentPage } from '@course/components/student-page';
import { defStudentRoute } from '@course/components/courses-app-router';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { Dropdown, MenuItem } from '@components/dropdown';
import { showToast } from '@components/toaster';
import { copyTrackerText } from '@course/components/tracker/glance';
import { togglePillClass } from '@course/components/toggle-pill';
import { IcoExclamation, IcoMail } from '@components/icons';
import { addDays } from 'shared/checkin-weeks';
import { participantChannels, sequenceChannel, TrackerChannel } from 'shared/tracker-config';
import { leadsSetUp, MAP_FIRST_CHANNEL_MESSAGE } from 'shared/tracker-leads';
import { firstTouchPerPerson, rollupWeek, TrackerWeekRollup } from 'shared/tracker-rollup';
import {
  ADD_BETS_FIRST_MESSAGE,
  betOf,
  channelOf,
  fmtTrackerDate,
  nextText,
  TrackerNextText,
  personName,
  personOfferText,
  sortGroup,
  stageLabel,
  stageRole,
  sortTouches,
  touchText,
  TRACKER_GROUP_ORDER,
  TRACKER_GROUP_TITLES,
  TrackerGroup,
  trackerGroup,
} from 'shared/tracker-people';
import {
  applyPersonResult,
  flashRow,
  focusRowAction,
  isTrackerOff,
  LINK_CLASS,
  Person,
  PersonResult,
  prefersReducedMotion,
  readError,
  readLocal,
  removePerson,
  stageChipClass,
  Touch,
  touchesByPerson,
  TrackerData,
  weekCaption,
  writeLocal,
} from './helpers';
import { LaterStep, LogResult, LogTouchPanel } from './log-touch-panel';
import { PersonPanel } from './person-panel';
import { AddPersonPanel, ImportPanel, PastePanel } from './add-panels';
import { ScoreboardCard, ScoreboardSetup, SetupBets } from './scoreboard';
import {
  BETS_HELP_SHORT,
  BetsCard,
  BetsForm,
  BetSwitch,
  SavedBets,
  saveBetFilter,
  savedBetFilter,
} from './bets';
import { Panel } from './panel';
import { ChannelsCard, ChannelsEditor, LeadsSaved, LeadsSetUpForm, SpendPanel } from './leads';
import { betData, breakdownBets, copyForClaude } from 'shared/tracker-summary';

export const route = defStudentRoute({
  load,
  Page,
  authLevel: 'student',
});

type AddPanel = 'add' | 'paste' | 'import';

type PanelState =
  | { kind: 'log'; personId: string; later?: LaterStep }
  | { kind: 'person'; personId: string }
  | { kind: AddPanel }
  /** "Add your bets first": opens instead of an add panel until there is a bet. */
  | { kind: 'bets'; then: AddPanel | { personId: string } }
  /** Leads: "Your channels", opened instead of an add panel until there is a channel. */
  | { kind: 'channels'; then: AddPanel }
  | { kind: 'spend' };

type PersonRow = Person;

type StatKey = 'sends' | 'replies' | 'held' | 'closes' | `stage:${string}`;

function csvUrl(courseId: string) {
  return `/csv/courses/${courseId}/tracker/mine.csv`;
}

function doneKey(courseId: string) {
  return `tracker-done-open:${courseId}`;
}

function Skeleton() {
  return (
    <div class="flex flex-col gap-4 animate-pulse" aria-busy="true" aria-label="Loading">
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} class="h-10 sm:h-20 rounded-lg bg-gray-100 dark:bg-gray-700" />
        ))}
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} class="h-14 rounded-lg bg-gray-100 dark:bg-gray-700" />
      ))}
    </div>
  );
}

/** Each stat's people this week: who is behind the number. */
function statPeople(data: TrackerData, stat: StatKey) {
  const { config, today, participant } = data;
  const now = rollupWeek(config, [], undefined, today);
  const start = now.weekStart;
  const end = addDays(start, 6);
  const inWeek = (t: Touch) => t.happenedOn >= start && t.happenedOn <= end;
  const ids = new Set<string>();
  if (stat === 'closes') {
    const firsts = firstTouchPerPerson(data.touches, (t) =>
      participant.closeAs === 'booked'
        ? t.kind === 'step' && stageRole(config, t.stageKey) === 'won'
        : participant.closeAs === 'collected' && t.kind === 'payment',
    );
    for (const t of firsts) {
      if (inWeek(t)) {
        ids.add(t.personId);
      }
    }
    return ids;
  }
  for (const t of data.touches) {
    if (t.kind !== 'step' || !inWeek(t)) {
      continue;
    }
    const role = stageRole(config, t.stageKey);
    const match =
      stat === 'sends'
        ? role === 'contacted'
        : stat === 'replies'
          ? role === 'replied' || role === 'replied_no'
          : stat === 'held'
            ? role === 'held'
            : stat === `stage:${t.stageKey}`;
    if (match) {
      ids.add(t.personId);
    }
  }
  return ids;
}

function Stat({
  value,
  label,
  lastWeek,
  extra,
  pressed,
  onToggle,
}: {
  value: number | string;
  label: string;
  lastWeek?: string;
  extra?: string;
  pressed: boolean;
  onToggle: () => void;
}) {
  const numberRef = useRef<HTMLSpanElement>(null);
  const previous = useRef(value);
  useEffect(() => {
    if (previous.current !== value && numberRef.current?.animate && !prefersReducedMotion()) {
      numberRef.current.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.08)' }, { transform: 'scale(1)' }],
        { duration: 300 },
      );
    }
    previous.current = value;
  }, [value]);
  return (
    <button
      type="button"
      aria-pressed={pressed}
      title={[lastWeek, extra].filter(Boolean).join(' · ') || undefined}
      class={`flex flex-row items-baseline sm:flex-col sm:items-start gap-2 sm:gap-0.5 rounded-lg border px-3 py-2 sm:p-3 text-left min-w-0 transition-colors ${
        pressed
          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
      }`}
      onClick={onToggle}
    >
      <span
        ref={numberRef}
        class="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums origin-left"
      >
        {value}
      </span>
      <span class="text-sm leading-tight text-gray-800 dark:text-gray-200 break-words min-w-0">
        {label}
      </span>
      {lastWeek && (
        <span class="hidden sm:block text-xs text-gray-500 dark:text-gray-400">{lastWeek}</span>
      )}
      {extra && (
        <span class="hidden sm:block text-xs text-gray-500 dark:text-gray-400">{extra}</span>
      )}
    </button>
  );
}

type Rollups = { now: TrackerWeekRollup; before: TrackerWeekRollup };

function TotalsStrip({
  data,
  rollups,
  channel,
  setChannelKey,
  filter,
  setFilter,
}: {
  data: TrackerData;
  rollups: Rollups;
  channel?: TrackerChannel;
  setChannelKey: (key: string) => void;
  filter?: StatKey;
  setFilter: (stat?: StatKey) => void;
}) {
  const { config, today, participant } = data;
  const { now, before } = rollups;
  const toggle = (stat: StatKey) => setFilter(filter === stat ? undefined : stat);
  const noCloseChoice = config.scoreboard.kind === 'revenue' && !participant.closeAs;
  const labels = config.rollupLabels;
  return (
    <section class="flex flex-col gap-2" aria-label="This week">
      <div class="flex items-baseline justify-between gap-2 flex-wrap">
        <p class="text-sm text-gray-600 dark:text-gray-400">{weekCaption(today)}</p>
        {!filter && (
          <p class="hidden sm:block text-xs text-gray-500 dark:text-gray-400">
            Tap a number to see who
          </p>
        )}
      </div>
      {config.channels.length > 1 && (
        <div class="flex flex-wrap gap-2" role="group" aria-label="Channel">
          {config.channels.map((c) => (
            <button
              key={c.key}
              type="button"
              aria-pressed={channel?.key === c.key}
              class={togglePillClass(channel?.key === c.key)}
              onClick={() => {
                setChannelKey(c.key);
                setFilter(undefined);
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {channel?.flow === 'sequence' ? (
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <Stat
            value={now.sends}
            label={labels.sends}
            lastWeek={`last week ${before.sends}`}
            pressed={filter === 'sends'}
            onToggle={() => toggle('sends')}
          />
          <Stat
            value={now.replies}
            label={labels.replies}
            lastWeek={`last week ${before.replies}`}
            pressed={filter === 'replies'}
            onToggle={() => toggle('replies')}
          />
          <Stat
            value={now.held}
            label={labels.held}
            lastWeek={`last week ${before.held}`}
            extra={`${now.booked} booked this week`}
            pressed={filter === 'held'}
            onToggle={() => toggle('held')}
          />
          <Stat
            value={noCloseChoice ? '–' : now.closes}
            label={labels.closes}
            lastWeek={noCloseChoice ? 'Set up your scoreboard' : `last week ${before.closes}`}
            pressed={filter === 'closes'}
            onToggle={() => toggle('closes')}
          />
        </div>
      ) : (
        channel && (
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {channel.stages.map((s) => (
              <Stat
                key={s.key}
                value={now.byStage[s.key] ?? 0}
                label={s.short || s.label}
                lastWeek={`last week ${before.byStage[s.key] ?? 0}`}
                pressed={filter === `stage:${s.key}`}
                onToggle={() => toggle(`stage:${s.key}`)}
              />
            ))}
          </div>
        )
      )}
    </section>
  );
}

function statLabel(data: TrackerData, stat: StatKey) {
  const labels = data.config.rollupLabels;
  if (stat.startsWith('stage:')) {
    const key = stat.slice(6);
    for (const c of data.config.channels) {
      const s = c.stages.find((x) => x.key === key);
      if (s) {
        return (s.short || s.label).toLowerCase();
      }
    }
    return '';
  }
  return labels[stat as keyof typeof labels].toLowerCase();
}

function statCount(rollup: TrackerWeekRollup, stat: StatKey) {
  if (stat.startsWith('stage:')) {
    return rollup.byStage[stat.slice(6)] ?? 0;
  }
  return rollup[stat as 'sends' | 'replies' | 'held' | 'closes'];
}

/** "3 replies this week · last week 6", plus bookings on the conversations stat. */
function chipText(data: TrackerData, { now, before }: Rollups, stat: StatKey) {
  const parts = [
    `${statCount(now, stat)} ${statLabel(data, stat)} this week`,
    `last week ${statCount(before, stat)}`,
  ];
  if (stat === 'held') {
    parts.push(`${now.booked} booked this week`);
  }
  return parts.join(' · ');
}

export function PersonRowView({
  data,
  person,
  next,
  group,
  openPerson,
  openLog,
  betShown,
}: {
  data: TrackerData;
  person: PersonRow;
  next: TrackerNextText;
  group?: TrackerGroup;
  openPerson: () => void;
  /** Absent in a coach's read-only view: no Log buttons. */
  openLog?: (payment: boolean) => void;
  /** The bet switch shows one bet, so naming it on every row is redundant. */
  betShown?: boolean;
}) {
  const { config } = data;
  const role = stageRole(config, person.stageKey);
  const channel = channelOf(config, person.channelKey);
  const isExit = role === 'replied_no' || role === 'lost';
  const isWon = role === 'won';
  const stepsDone =
    channel?.flow === 'steps' && channel.stages[channel.stages.length - 1]?.key === person.stageKey;
  const urgent = group === 'overdue' || group === 'due_today';
  const name = personName(person);
  const line2 = [
    betShown ? '' : personOfferText(config, data.bets, person),
    config.channels.length > 1 ? channel?.label : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <li
      data-person-row={person.id}
      class="flex items-center gap-3 min-h-14 px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
    >
      <button
        type="button"
        class="grow min-w-0 grid grid-cols-1 sm:grid-cols-2 sm:items-center gap-1 sm:gap-3 text-left"
        onClick={openPerson}
      >
        <span class="flex flex-col min-w-0">
          <span class="font-semibold text-gray-900 dark:text-gray-100 truncate" title={name}>
            {name}
          </span>
          {line2 && <span class="text-sm text-gray-600 dark:text-gray-400 truncate">{line2}</span>}
        </span>
        <span class="flex flex-wrap sm:flex-nowrap items-center gap-x-2 gap-y-1 min-w-0 text-sm">
          <span
            class={`shrink-0 whitespace-nowrap px-2 py-0.5 border rounded-full text-xs font-medium ${stageChipClass(
              config,
              person.stageKey,
            )}`}
          >
            {stageLabel(config, person.stageKey)}
          </span>
          {next.text && (
            <span
              title={next.text}
              class={`min-w-0 sm:truncate ${
                next.isLate
                  ? 'text-red-700 dark:text-red-400'
                  : next.isMuted
                    ? 'text-gray-500 dark:text-gray-400'
                    : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              {next.text}
            </span>
          )}
        </span>
      </button>
      {openLog && config.fields.email && !person.email && (
        <span class="hidden sm:block shrink-0 w-11" aria-hidden="true" />
      )}
      {openLog && config.fields.email && person.email && (
        <a
          href={`mailto:${person.email}`}
          class="shrink-0 inline-flex items-center justify-center min-h-11 min-w-11 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          aria-label={`Email ${name}`}
          title={person.email}
        >
          <IcoMail class="w-5 h-5" />
        </a>
      )}
      {openLog && !isExit && !stepsDone && (
        <span class="shrink-0">
          {urgent && !isWon ? (
            <BtnPrimary
              data-person-action={person.id}
              class="min-h-11 min-w-11"
              onClick={() => openLog(false)}
            >
              Log touch
            </BtnPrimary>
          ) : (
            <BtnSecondary
              data-person-action={person.id}
              class="min-h-11 min-w-11"
              onClick={() => openLog(isWon)}
            >
              {isWon ? 'Log payment' : 'Log touch'}
            </BtnSecondary>
          )}
        </span>
      )}
    </li>
  );
}

/**
 * Leads, before anyone is added: "Set up your tracker" (week one) until it
 * is saved, then "Your channels" (week two), each ahead of the add steps
 * while it is still to do.
 */
function LeadsSetUp({
  data,
  courseId,
  onLeads,
  children,
}: {
  data: TrackerData;
  courseId: UUID;
  onLeads: (saved: LeadsSaved) => void;
  children: ComponentChildren;
}) {
  const setUp = leadsSetUp(data.participant);
  const mapped = data.participant.funnels.length > 0;
  const setUpCard = !setUp && (
    <LeadsSetUpForm
      courseId={courseId}
      participant={data.participant}
      hasHours={false}
      isChange={false}
      onSaved={onLeads}
    />
  );
  const channels = <ChannelsCard courseId={courseId} data={data} onSaved={onLeads} />;
  return (
    <div class="flex flex-col gap-6">
      {setUpCard}
      {!mapped && channels}
      {children}
      {mapped && channels}
    </div>
  );
}

function EmptyState({
  data,
  courseId,
  open,
  onParticipant,
  setupBets,
  onLeads,
}: {
  data: TrackerData;
  courseId: UUID;
  open: (panel: AddPanel) => void;
  onParticipant: (p: TrackerData['participant']) => void;
  setupBets?: SetupBets;
  onLeads: (saved: LeadsSaved) => void;
}) {
  const [keepSetup, setKeepSetup] = useState(false);
  const cards: Array<{ title: string; action: () => void; label: string }> = [
    { title: 'Add one person.', label: 'Add person', action: () => open('add') },
    { title: 'Paste a list.', label: 'Paste a list', action: () => open('paste') },
  ];
  if (sequenceChannel(data.config)) {
    cards.push({
      title: 'Import your plan file or RIS sheet.',
      label: 'Import',
      action: () => open('import'),
    });
  }
  const revenue = data.config.scoreboard.kind === 'revenue';
  const needsBets = !!setupBets && !setupBets.list.length;
  const settingUp = revenue && (!data.participant.closeAs || keepSetup);
  let setup;
  if (settingUp) {
    setup = (
      <ScoreboardSetup
        courseId={courseId}
        participant={data.participant}
        isChange={false}
        bets={setupBets}
        onSaved={(p) => {
          setKeepSetup(false);
          onParticipant(p);
        }}
        onPartial={(p) => {
          setKeepSetup(true);
          onParticipant(p);
        }}
        onCancel={keepSetup ? () => setKeepSetup(false) : undefined}
      />
    );
  } else if (setupBets && (!revenue || needsBets)) {
    setup = (
      <BetsCard
        courseId={courseId}
        bets={setupBets.list}
        people={setupBets.people}
        onBets={setupBets.onChange}
      />
    );
  }
  const setupFirst = !!setup && (needsBets || settingUp);
  const addSteps = (
    <>
      <h2 class="text-xl text-gray-900 dark:text-gray-100">Add the people you plan to contact</h2>
      <ol class="grid gap-3 sm:grid-cols-3">
        {cards.map((c, i) => (
          <li
            key={c.label}
            class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-col gap-3"
          >
            <span class="inline-flex items-center justify-center size-7 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-sm font-semibold">
              {i + 1}
            </span>
            <span class="text-sm text-gray-900 dark:text-gray-100">{c.title}</span>
            {i === 0 ? (
              <BtnPrimary onClick={c.action}>{c.label}</BtnPrimary>
            ) : (
              <BtnSecondary onClick={c.action}>{c.label}</BtnSecondary>
            )}
          </li>
        ))}
      </ol>
    </>
  );
  if (participantChannels(data.config)) {
    return (
      <LeadsSetUp data={data} courseId={courseId} onLeads={onLeads}>
        {setupBets && (
          <BetsCard
            courseId={courseId}
            bets={setupBets.list}
            people={setupBets.people}
            onBets={setupBets.onChange}
          />
        )}
        {addSteps}
      </LeadsSetUp>
    );
  }
  const planHint = settingUp && sequenceChannel(data.config) && (
    <p class="text-sm text-gray-700 dark:text-gray-300">
      Have the tracker file from your plan?{' '}
      <Button class={LINK_CLASS} onClick={() => open('import')}>
        Import it
      </Button>{' '}
      to fill in your setup and people at once.
    </p>
  );
  return (
    <div class="flex flex-col gap-6">
      {setupFirst && planHint}
      {setupFirst && setup}
      {addSteps}
      {!setupFirst && setup}
    </div>
  );
}

/** "Logged", with "Omar: Conversation booked for Thu, Nov 19." naming the last thing the save wrote. */
function logToast(data: TrackerData, result: LogResult) {
  const created = sortTouches(result.touches).filter((t) => result.touchIds.includes(t.id));
  const last = created[created.length - 1];
  const line = last ? touchText(data.config, last, data.today) : 'Logged';
  return { title: 'Logged', message: `${result.person.firstName}: ${line}.` };
}

function Page(props: LoadedProps<typeof load>) {
  const { course } = props.state;
  const courseId = course.id as UUID;
  const [data, setData] = useState<TrackerData | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [off, setOff] = useState(false);
  const [panel, setPanel] = useState<PanelState | undefined>();
  const [filter, setFilter] = useState<StatKey | undefined>();
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [allPeople, setAllPeople] = useState(false);
  const [doneOpen, setDoneOpen] = useState(() => readLocal(doneKey(course.id)) === '1');
  const [channelKey, setChannelKey] = useState<string | undefined>();
  const [betFilter, setBetFilter] = useState<string | undefined>();
  const betFilterRead = useRef(false);

  function handleError(err: unknown) {
    if (isTrackerOff(err)) {
      setOff(true);
      setPanel(undefined);
      return true;
    }
    return false;
  }

  async function reload() {
    setLoadError(undefined);
    try {
      setData(await rpx.tracker.getMyTracker({ courseId }));
    } catch (err) {
      if (!handleError(err)) {
        setLoadError('Couldn’t load your tracker. Check your connection and try again');
      }
    }
  }

  useEffect(() => {
    reload();
  }, [courseId]);

  // Home's Log touch links here with ?log=<personId>. Read it once, on the
  // first load, then drop it so a refresh or Back doesn't reopen the panel.
  const logParamRead = useRef(false);
  useEffect(() => {
    if (logParamRead.current || (!data && !off)) {
      return;
    }
    logParamRead.current = true;
    const url = new URL(window.location.href);
    const personId = url.searchParams.get('log');
    if (!personId) {
      return;
    }
    if (data?.people.some((p) => p.id === personId)) {
      setPanel({ kind: 'log', personId });
    }
    url.searchParams.delete('log');
    window.history.replaceState(window.history.state, document.title, url.toString());
  }, [data, off]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), 150);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (betFilterRead.current || !data) {
      return;
    }
    betFilterRead.current = true;
    setBetFilter(savedBetFilter(breakdownBets(data), course.id));
  }, [data]);

  const switchBets = data ? breakdownBets(data) : [];
  const shownBet = betOf(switchBets, betFilter);
  const viewData = useMemo(
    () => (data && shownBet ? betData(data, shownBet.id) : data),
    [data, shownBet?.id],
  );

  function chooseBet(betId?: string) {
    setBetFilter(betId);
    saveBetFilter(course.id, betId);
    setFilter(undefined);
  }

  /**
   * Add person, Paste a list and Import open the bets form until there is a
   * bet, and on Leads "Your channels" until there is a channel.
   */
  function openAdd(kind: AddPanel) {
    if (data && participantChannels(data.config) && !data.config.channels.length) {
      setPanel({ kind: 'channels', then: kind });
      return;
    }
    if (data?.config.bets && !data.bets.length) {
      setPanel({ kind: 'bets', then: kind });
      return;
    }
    setPanel({ kind });
  }

  /** New bets, with the people a removal moved onto another bet. */
  function setBets({ bets, moved }: SavedBets) {
    setData((d) => {
      if (!d) {
        return d;
      }
      const to = new Map(moved.map((m) => [m.from, m.to]));
      const people = d.people.map((p) =>
        p.betId && to.has(p.betId) ? { ...p, betId: to.get(p.betId) } : p,
      );
      return { ...d, bets, people };
    });
  }

  /** A Leads set-up or channel save: the participant, their config, and what moved. */
  function setLeads(saved: LeadsSaved) {
    setData((d) =>
      d
        ? {
            ...d,
            participant: saved.participant,
            config: saved.config,
            people: saved.people ?? d.people,
            spend: saved.spend ?? d.spend,
          }
        : d,
    );
  }

  const setupBets: SetupBets | undefined = data?.config.bets
    ? { list: data.bets, people: data.people, onChange: setBets }
    : undefined;

  useEffect(() => {
    function onRejection(e: PromiseRejectionEvent) {
      if (isTrackerOff(e.reason)) {
        e.preventDefault();
        setOff(true);
        setPanel(undefined);
      }
    }
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, []);

  const byPerson = useMemo(() => touchesByPerson(data?.touches ?? []), [data?.touches]);
  const rollups = useMemo<Rollups | undefined>(() => {
    if (!viewData) {
      return;
    }
    const closeAs = viewData.participant.closeAs;
    return {
      now: rollupWeek(viewData.config, viewData.touches, closeAs, viewData.today),
      before: rollupWeek(viewData.config, viewData.touches, closeAs, addDays(viewData.today, -7)),
    };
  }, [viewData?.config, viewData?.touches, viewData?.participant.closeAs, viewData?.today]);
  const nextTexts = useMemo(() => {
    const texts = new Map<string, TrackerNextText>();
    for (const p of data?.people ?? []) {
      texts.set(
        p.id,
        nextText(data!.config, p, byPerson.get(p.id) ?? [], data!.today, data!.participant.closeAs),
      );
    }
    return texts;
  }, [data?.people, byPerson, data?.config, data?.today, data?.participant.closeAs]);

  function applyResult(result: PersonResult) {
    setData((d) => (d ? applyPersonResult(d, result) : d));
  }

  function closePanel(focusPersonId?: string) {
    setPanel(undefined);
    if (focusPersonId) {
      focusRowAction(focusPersonId);
    }
  }

  function onLogged(result: LogResult) {
    if (!data) {
      return;
    }
    setData((d) => (d ? applyPersonResult(d, result) : d));
    const personId = result.person.id;
    closePanel(personId);
    flashRow(personId);
    const { title, message } = logToast(data, result);
    showToast({
      type: 'ok',
      title,
      message,
      timeout: 8000,
      action: {
        label: 'Undo',
        onClick: () => {
          rpx.tracker
            .deleteTouches({
              courseId,
              touchIds: result.touchIds as UUID[],
              restoreNext: {
                personId: personId as UUID,
                next: {
                  step: result.previousNext.step,
                  stepKey: result.previousNext.stepKey,
                  on: result.previousNext.on,
                },
              },
            })
            .then((results) => {
              results.forEach(applyResult);
              flashRow(personId);
            })
            .catch((err) => {
              if (!handleError(err)) {
                showToast({
                  type: 'warn',
                  title: 'Couldn’t undo',
                  message: readError(err).message,
                });
              }
            });
        },
      },
    });
  }

  async function copySummary() {
    if (!data) {
      return;
    }
    await copyTrackerText(copyForClaude(data), 'Copied. Paste it into your Claude chat.');
  }

  const today = data?.today ?? '';
  const ended = !!data?.coachAccessEnded;
  const selectedChannel = data
    ? (data.config.channels.find((c) => c.key === channelKey) ?? data.config.channels[0])
    : undefined;

  const view = useMemo(() => {
    if (!data || !viewData) {
      return;
    }
    const people = viewData.people as PersonRow[];
    const q = search.toLowerCase();
    if (q) {
      const matches = people
        .filter((p) => {
          const bet = betOf(data.bets, p.betId);
          return [
            personName(p),
            p.firstName,
            p.offer,
            p.email,
            p.note,
            bet?.name ?? '',
            bet?.offer ?? '',
          ].some((f) => f.toLowerCase().includes(q));
        })
        .sort((a, b) => personName(a).localeCompare(personName(b)));
      return { kind: 'flat' as const, people: matches };
    }
    const filterIds = filter ? statPeople(viewData, filter) : undefined;
    const visible = filterIds ? people.filter((p) => filterIds.has(p.id)) : people;
    if (allPeople) {
      return {
        kind: 'flat' as const,
        people: [...visible].sort((a, b) => personName(a).localeCompare(personName(b))),
      };
    }
    const groups = new Map<TrackerGroup, PersonRow[]>();
    for (const p of visible) {
      const g = trackerGroup(
        data.config,
        p,
        byPerson.get(p.id) ?? [],
        data.participant.closeAs,
        data.today,
      );
      groups.set(g, [...(groups.get(g) ?? []), p]);
    }
    return {
      kind: 'groups' as const,
      groups: TRACKER_GROUP_ORDER.filter((g) => groups.get(g)?.length).map((g) => ({
        group: g,
        people: sortGroup(data.config, g, groups.get(g)!, byPerson) as PersonRow[],
      })),
      count: visible.length,
    };
  }, [data, viewData, search, filter, allPeople, byPerson]);

  const panelPerson =
    panel && 'personId' in panel
      ? (data?.people.find((p) => p.id === panel.personId) as PersonRow | undefined)
      : undefined;

  function renderPanel() {
    if (!data || !panel) {
      return;
    }
    if (panel.kind === 'log' && panelPerson) {
      const channel = channelOf(data.config, panelPerson.channelKey);
      if (!channel) {
        return;
      }
      return (
        <LogTouchPanel
          data={data}
          courseId={courseId}
          person={panelPerson}
          touches={byPerson.get(panelPerson.id) ?? []}
          channel={channel}
          initialLater={panel.later}
          onLogged={onLogged}
          close={() => closePanel(panelPerson.id)}
        />
      );
    }
    if (panel.kind === 'person' && panelPerson) {
      return (
        <PersonPanel
          data={data}
          courseId={courseId}
          person={panelPerson}
          touches={byPerson.get(panelPerson.id) ?? []}
          onResult={applyResult}
          onDeleted={(id) => {
            setData((d) => (d ? removePerson(d, id) : d));
            setPanel(undefined);
            showToast({ type: 'ok', title: `Deleted ${personName(panelPerson)}`, message: '' });
          }}
          openLog={(payment) =>
            setPanel({
              kind: 'log',
              personId: panelPerson.id,
              later: payment ? 'payment' : undefined,
            })
          }
          close={() => closePanel(panelPerson.id)}
          openBets={() => setPanel({ kind: 'bets', then: { personId: panelPerson.id } })}
        />
      );
    }
    if (panel.kind === 'bets') {
      const then = panel.then;
      return (
        <Panel
          title="Your bets"
          header={<p class="text-sm text-gray-700 dark:text-gray-300">{ADD_BETS_FIRST_MESSAGE}</p>}
          close={() => setPanel(undefined)}
        >
          <BetsForm
            courseId={courseId}
            bets={data.bets}
            people={data.people}
            saveLabel="Save and continue"
            help={BETS_HELP_SHORT}
            onSaved={(saved) => {
              setBets(saved);
              setPanel(
                typeof then === 'string'
                  ? { kind: then }
                  : { kind: 'person', personId: then.personId },
              );
            }}
            onChanged={setBets}
          />
        </Panel>
      );
    }
    if (panel.kind === 'channels') {
      const then = panel.then;
      return (
        <Panel
          title="Your channels"
          header={
            <p class="text-sm text-gray-700 dark:text-gray-300">{MAP_FIRST_CHANNEL_MESSAGE}</p>
          }
          close={() => setPanel(undefined)}
        >
          <ChannelsEditor
            courseId={courseId}
            data={data}
            onSaved={setLeads}
            onDone={(saved) => setPanel(saved ? { kind: then } : undefined)}
          />
        </Panel>
      );
    }
    if (panel.kind === 'spend') {
      return (
        <SpendPanel
          courseId={courseId}
          data={data}
          onSpend={(spend) => setData((d) => (d ? { ...d, spend } : d))}
          close={() => setPanel(undefined)}
        />
      );
    }
    if (panel.kind === 'add') {
      return (
        <AddPersonPanel
          data={data}
          courseId={courseId}
          onAdded={(result) => {
            applyResult(result);
            flashRow(result.person.id);
          }}
          openPerson={(id) => setPanel({ kind: 'person', personId: id })}
          close={() => setPanel(undefined)}
        />
      );
    }
    if (panel.kind === 'paste') {
      return (
        <PastePanel
          data={data}
          courseId={courseId}
          onAdded={() => reload()}
          close={() => setPanel(undefined)}
        />
      );
    }
    if (panel.kind === 'import') {
      return (
        <ImportPanel
          data={data}
          courseId={courseId}
          onImported={() => reload()}
          close={() => setPanel(undefined)}
        />
      );
    }
  }

  function openLog(person: Person, payment: boolean) {
    setPanel({ kind: 'log', personId: person.id, later: payment ? 'payment' : undefined });
  }

  return (
    <StudentPage course={course} currentLink="tracker">
      <div class="max-w-[760px] w-full mx-auto px-4 sm:px-6 flex flex-col gap-4 sm:gap-6 pb-16">
        <h1 class="text-2xl text-gray-900 dark:text-gray-100">Tracker</h1>
        {off && (
          <p class="text-gray-700 dark:text-gray-300">
            The tracker is switched off for this course. Ask your coach if you need it back.
          </p>
        )}
        {!off && loadError && (
          <div class="flex flex-col items-start gap-3" role="alert">
            <p class="text-gray-700 dark:text-gray-300">{loadError}</p>
            <BtnSecondary onClick={reload}>Try again</BtnSecondary>
          </div>
        )}
        {!off && !loadError && !data && <Skeleton />}
        {!off && data && (
          <>
            {ended && data.endDate && (
              <div class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
                <p class="text-sm text-gray-800 dark:text-gray-200">
                  The immersion sprint ended {fmtTrackerDate(data.endDate, today)}. Your tracker
                  stays yours. Your coach no longer sees it.
                </p>
                <BtnSecondary href={csvUrl(course.id)} download="my-tracker.csv">
                  Download CSV
                </BtnSecondary>
              </div>
            )}
            {data.people.length === 0 ? (
              <EmptyState
                data={data}
                courseId={courseId}
                open={openAdd}
                onParticipant={(participant) => setData((d) => (d ? { ...d, participant } : d))}
                setupBets={setupBets}
                onLeads={setLeads}
              />
            ) : (
              <>
                {switchBets.length > 0 && (
                  <BetSwitch bets={switchBets} value={shownBet?.id} onChange={chooseBet} />
                )}
                <TotalsStrip
                  data={viewData!}
                  rollups={rollups!}
                  channel={selectedChannel}
                  setChannelKey={setChannelKey}
                  filter={filter}
                  setFilter={setFilter}
                />
                <ScoreboardCard
                  data={data}
                  courseId={courseId}
                  bets={setupBets}
                  onParticipant={(participant) => setData((d) => (d ? { ...d, participant } : d))}
                  leads={
                    participantChannels(data.config)
                      ? { onSaved: setLeads, openSpend: () => setPanel({ kind: 'spend' }) }
                      : undefined
                  }
                />
                {participantChannels(data.config) && (
                  <ChannelsCard courseId={courseId} data={data} onSaved={setLeads} />
                )}
                {data.config.scoreboard.kind === 'cpl' && setupBets && (
                  <BetsCard
                    courseId={courseId}
                    bets={data.bets}
                    people={data.people}
                    onBets={setBets}
                  />
                )}
                <section class="flex flex-col gap-3" aria-label="People">
                  <div class="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div class="flex items-center gap-3 grow">
                      <input
                        type="search"
                        class="w-full sm:max-w-64 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
                        placeholder="Search people"
                        aria-label="Search people"
                        value={searchText}
                        onInput={(e) => setSearchText(e.currentTarget.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setSearchText('');
                            setSearch('');
                          }
                        }}
                      />
                      <button
                        type="button"
                        aria-pressed={allPeople}
                        class={`hidden sm:inline-flex shrink-0 px-3 py-2 rounded-md text-sm border ${
                          allPeople
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                        onClick={() => setAllPeople(!allPeople)}
                      >
                        All people
                      </button>
                    </div>
                    <div class="flex items-center gap-2">
                      <BtnPrimary onClick={() => openAdd('add')}>Add person</BtnPrimary>
                      <BtnSecondary class="hidden sm:inline-flex" onClick={() => openAdd('paste')}>
                        Paste a list
                      </BtnSecondary>
                      <Dropdown
                        triggerClass="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300"
                        renderMenu={() => (
                          <div class="flex flex-col">
                            <MenuItem
                              regularCase
                              class="sm:hidden"
                              onClick={() => openAdd('paste')}
                            >
                              Paste a list
                            </MenuItem>
                            <MenuItem
                              regularCase
                              onClick={() => setAllPeople(!allPeople)}
                              class="sm:hidden"
                            >
                              {allPeople ? 'Grouped view' : 'All people'}
                            </MenuItem>
                            <MenuItem regularCase onClick={copySummary}>
                              Copy for Claude
                            </MenuItem>
                            <MenuItem
                              regularCase
                              href={csvUrl(course.id)}
                              download="my-tracker.csv"
                            >
                              Download CSV
                            </MenuItem>
                            {sequenceChannel(data.config) && (
                              <MenuItem regularCase onClick={() => openAdd('import')}>
                                Import
                              </MenuItem>
                            )}
                          </div>
                        )}
                      >
                        More
                      </Dropdown>
                    </div>
                  </div>
                  {search && view?.kind === 'flat' && (
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                      {view.people.length} of {viewData!.people.length} people matching “{search}”
                    </p>
                  )}
                  {filter && !search && rollups && (
                    <p class="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <span class="px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950 border border-indigo-200 dark:border-indigo-800 text-indigo-800 dark:text-indigo-200">
                        {chipText(viewData!, rollups, filter)} ·{' '}
                        <Button class="underline" onClick={() => setFilter(undefined)}>
                          Clear
                        </Button>
                      </span>
                    </p>
                  )}
                  {filter && !search && view && 'count' in view && view.count === 0 && (
                    <p class="text-sm text-gray-600 dark:text-gray-400">
                      No {statLabel(data, filter)} this week yet. Log a touch and it shows here.
                    </p>
                  )}
                  {view?.kind === 'flat' && (
                    <ul class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                      {view.people.map((p) => (
                        <PersonRowView
                          key={p.id}
                          data={data}
                          person={p}
                          next={nextTexts.get(p.id)!}
                          openPerson={() => setPanel({ kind: 'person', personId: p.id })}
                          openLog={(payment) => openLog(p, payment)}
                          betShown={!!shownBet}
                        />
                      ))}
                    </ul>
                  )}
                  {view?.kind === 'groups' &&
                    view.groups.map(({ group, people }) => {
                      const isDone = group === 'done';
                      const steps =
                        selectedChannel?.flow === 'steps' && !sequenceChannel(data.config);
                      const reached = participantChannels(data.config)
                        ? `reached ${data.participant.destination || 'your destination'}`
                        : 'last stage reached';
                      const title = isDone
                        ? `Done (${people.length} · ${steps ? reached : 'won, lost or replied no'})`
                        : `${TRACKER_GROUP_TITLES[group]} (${people.length})`;
                      return (
                        <section key={group} class="flex flex-col gap-2">
                          {isDone ? (
                            <Button
                              class="self-start text-sm font-semibold text-gray-700 dark:text-gray-300"
                              aria-expanded={doneOpen}
                              onClick={() => {
                                writeLocal(doneKey(course.id), doneOpen ? '0' : '1');
                                setDoneOpen(!doneOpen);
                              }}
                            >
                              {doneOpen ? '▾' : '▸'} {title}
                            </Button>
                          ) : (
                            <h2
                              class={`flex items-center gap-1 text-sm font-semibold ${
                                group === 'overdue'
                                  ? 'text-red-700 dark:text-red-400'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {group === 'overdue' && <IcoExclamation class="size-4" />}
                              {title}
                            </h2>
                          )}
                          {(!isDone || doneOpen) && (
                            <ul class="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                              {people.map((p) => (
                                <PersonRowView
                                  key={p.id}
                                  data={data}
                                  person={p}
                                  next={nextTexts.get(p.id)!}
                                  group={group}
                                  openPerson={() => setPanel({ kind: 'person', personId: p.id })}
                                  openLog={(payment) => openLog(p, payment)}
                                  betShown={!!shownBet}
                                />
                              ))}
                            </ul>
                          )}
                        </section>
                      );
                    })}
                </section>
              </>
            )}
          </>
        )}
      </div>
      {renderPanel()}
    </StudentPage>
  );
}

async function load(route: RouteLoadProps) {
  const { courseId } = route.params;
  const course = await rpx.courses.getStudentCourse({ id: courseId });
  return { course };
}
